import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/serverConfig';
import './CheckoutForm.css';

/* eslint-disable react-hooks/exhaustive-deps, no-mixed-operators */

// CheckoutForm
// - Loads available plan -> price mappings from `/api/stripe/price-map`.
// - Lets user select a plan, enter email, accept contract checkbox.
// - Calls POST `/api/stripe/create-checkout-session` and redirects to the returned URL.

export default function CheckoutForm({ returnUrlBase, initialPlanId, planDefinitions = [] }) {
  const [mapping, setMapping] = useState({});
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(initialPlanId || '');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastTriedUrl, setLastTriedUrl] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    async function load() {
      const tried = [];
      const candidates = [];
      try {
        const apiUrl = getApiUrl('/api/stripe/price-map');
        candidates.push(apiUrl);
      } catch (e) {
        // ignore
      }
      // relative path (same-origin proxy)
      candidates.push('/api/stripe/price-map');
      // localhost fallback (use common dev port)
      const port = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_PORT) || '50008';
      candidates.push(`http://localhost:${port}/api/stripe/price-map`);

      let lastErr = null;
      for (const url of candidates) {
        try {
          tried.push(url);
          const res = await fetch(url, { credentials: 'include' });
          if (!res.ok) throw new Error('status ' + res.status);
          const j = await res.json();
          const map = (j && j.mapping) || {};
          setMapping(map);
          const defs = (planDefinitions || []).reduce((m, p) => { m[p.id] = p; return m; }, {});
          const arr = Object.keys(map).map((k) => {
            const raw = map[k];
            let priceId = '';
            let overrideDisplay = '';
            if (raw && typeof raw === 'string') {
              priceId = raw;
            } else if (raw && typeof raw === 'object') {
              priceId = raw.price || raw.priceId || '';
              overrideDisplay = raw.display || raw.priceDisplay || '';
            }
            return {
              id: k,
              priceId,
              label: (defs[k] && defs[k].label) || k,
              priceDisplay: (defs[k] && defs[k].priceDisplay) || overrideDisplay || (priceId ? `— ${priceId}` : '')
            };
          });
          setPlans(arr);
          if (initialPlanId && arr.find(a => a.id === initialPlanId)) {
            setPlanId(initialPlanId);
          } else if (arr.length && !planId) {
            setPlanId(arr[0].id);
          }
          setError(null);
          setLastTriedUrl(null);
          return;
        } catch (e) {
          lastErr = e;
          console.debug('CheckoutForm: price-map fetch failed for', url, e && (e.message || e));
          continue;
        }
      }
      // all candidates failed
      setMapping({});
      setPlans([]);
      setError('Failed to load price map: ' + (lastErr ? (lastErr.message || String(lastErr)) : ''));
      setLastTriedUrl((tried && tried.length) ? tried[tried.length - 1] : null);
    }
    load();
  }, []);

  // Fetch human-friendly price labels for any plans that only have a raw price id.
  useEffect(() => {
    let mounted = true;
    async function enrichPrices() {
      try {
        if (!plans || plans.length === 0) return;
        // Find plans that look like they need enrichment (priceDisplay empty or shows a raw price id)
        const need = plans.filter(p => !p.priceDisplay || /^\s*—\s*price_/i.test(p.priceDisplay) || p.priceDisplay.toLowerCase().includes('price not set'));
        if (!need.length) return;

        const candidates = [];
        try { candidates.push(getApiUrl('/api/stripe/prices')); } catch (e) { /* ignore */ }
        candidates.push('/api/stripe/prices');
        const port = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_PORT) || '50008';
        candidates.push(`http://localhost:${port}/api/stripe/prices`);

        let respJson = null;
        for (const url of candidates) {
          try {
            const res = await fetch(url, { credentials: 'include' });
            if (!res.ok) continue;
            const j = await res.json().catch(() => null);
            if (!j) continue;
            // router returns { success: true, data: resp }, where resp.data is the Stripe array
            const pricesResp = j && j.data ? j.data : j;
            // Stripe API returns an object with `data` array
            const list = (pricesResp && pricesResp.data) || pricesResp || [];
            // build a map of priceId -> priceObj
            const map = {};
            (Array.isArray(list) ? list : []).forEach(p => { if (p && p.id) map[p.id] = p; });

            // update plans with formatted labels where possible
            const updated = plans.map(pl => {
              try {
                const pid = pl.priceId;
                if (!pid) return pl;
                const priceObj = map[pid];
                if (!priceObj) return pl;
                // format amount
                let display = '';
                if (typeof priceObj.unit_amount === 'number') {
                  try {
                    const amt = (priceObj.unit_amount / 100);
                    const currency = (priceObj.currency || 'usd').toUpperCase();
                    display = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amt);
                  } catch (e) {
                    display = (priceObj.unit_amount / 100) + ' ' + (priceObj.currency || 'USD');
                  }
                }
                // handle recurring interval
                if (priceObj.recurring && priceObj.recurring.interval) {
                  const interval = priceObj.recurring.interval; // day/week/month/year
                  const count = priceObj.recurring.interval_count || 1;
                  const freq = count > 1 ? `${count} ${interval}s` : interval;
                  display = display ? `${display} / ${freq}` : `${freq}`;
                } else {
                  // one-time price (no recurring)
                  // keep display as plain amount
                }

                return { ...pl, priceDisplay: display || pl.priceDisplay };
              } catch (err) { return pl; }
            });

            if (mounted) setPlans(updated);
            respJson = true;
            break;
          } catch (e) {
            // try next candidate
            continue;
          }
        }
        return respJson;
      } catch (e) {
        // ignore enrichment failures — UI will show price id fallback
      }
    }
    enrichPrices();
    return () => { mounted = false; };
  }, [plans]);

  const createSession = async (ev) => {
    ev && ev.preventDefault();
    setError(null);
    setResult(null);
    if (!planId) return setError('Select a plan');
    // Ensure the selected plan has a mapped Stripe price id
    const rawSelected = mapping && mapping[planId];
    const selected = rawSelected ? (typeof rawSelected === 'string' ? rawSelected : (rawSelected.price || rawSelected.priceId || '')) : '';
    if (!selected || !selected.toString().trim()) return setError('Selected plan has no Stripe price configured. Please contact admin.');
    if (!agreed) return setError('You must accept the contract');
    setBusy(true);
    try {
      const body = { planId, email };
      if (returnUrlBase) body.returnUrlBase = returnUrlBase;

      const tried = [];
      const candidates = [];
      // Try same-origin relative path first (works when app is proxied)
      candidates.push('/api/stripe/create-checkout-session');
      // Then try configured API URL
      try { candidates.push(getApiUrl('/api/stripe/create-checkout-session')); } catch (e) { /* ignore */ }
      // localhost fallback using env port (defensive)
      const port = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_PORT) || '50008';
      candidates.push(`http://localhost:${port}/api/stripe/create-checkout-session`);

      let lastErr = null;
      let j = null;
      let res = null;

      for (const url of candidates) {
        try {
          tried.push(url);
          res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            credentials: 'include'
          });
          // record attempted URL (not stored)
          // Try to parse JSON even on non-OK for better error messages
          j = await res.json().catch(() => null);
          if (!res.ok) {
            lastErr = new Error(`status ${res.status}`);
            // continue to next candidate
            continue;
          }
          // success
          setResult(j);
          break;
        } catch (err) {
          lastErr = err;
          console.debug('createSession: fetch failed for', url, err && (err.message || err));
          continue;
        }
      }

      if (!res || !res.ok) {
        // Extract detailed error message from response
        let errMsg = 'Failed to create session';
        
        if (j && j.error) {
          errMsg = j.error;
          
          // Add helpful details if available
          if (j.detail) {
            errMsg += `: ${j.detail}`;
          }
          
          // Show hint for common issues
          if (j.hint) {
            errMsg += `\n\nℹ️ ${j.hint}`;
          }
          
          // Special handling for Stripe configuration errors
          if (j.error.includes('API key') || j.error.includes('STRIPE_SECRET_KEY')) {
            errMsg = '⚠️ Payment system configuration error. Please contact the administrator to update Stripe API keys.';
          }
        } else if (lastErr) {
          errMsg = lastErr.message || String(lastErr);
        }
        
        console.error('Checkout session creation failed:', {
          status: res?.status,
          error: j,
          triedUrls: tried
        });
        
        setError(errMsg);
        setLastTriedUrl((tried && tried.length) ? tried[tried.length - 1] : null);
        setBusy(false);
        return;
      }
      // If Stripe returned a hosted URL, redirect there.
      if (j && j.url) {
        window.location.href = j.url;
        return;
      }
      // Fallback: if sessionId returned and Stripe.js is available, try redirectToCheckout
      if (j.sessionId) {
        const pk = window.STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
        if (pk && window.Stripe) {
          const stripe = window.Stripe(pk);
          const { error: err } = await stripe.redirectToCheckout({ sessionId: j.sessionId });
          if (err) setError(err.message || 'Stripe redirect failed');
          return;
        }
        // If Stripe.js not available, attempt to open checkout by session URL pattern
        // (server is expected to return `url` normally). Otherwise display session.
      }
    } catch (e) {
      console.error('createSession error', e);
      setError(String(e && (e.message || e)));
    } finally {
      setBusy(false);
    }
  };

  const hasSelectedPrice = () => {
    try {
      if (!planId) return false;
      const raw = mapping && mapping[planId];
      const v = raw ? (typeof raw === 'string' ? raw : (raw.price || raw.priceId || '')) : '';
      return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
    } catch (e) { return false; }
  };

  return (
    <div className="checkout-form">
      <h3>Secure Checkout</h3>

      {/* Visual plan cards for quicker selection */}
      {plans && plans.length > 0 && (
        <div className="checkout-form__plans" aria-hidden={false}>
          {plans.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              className={`checkout-plan-card ${planId === p.id ? 'selected' : ''}`}
              onClick={() => setPlanId(p.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPlanId(p.id); } }}
            >
              <div className="label">{p.label}</div>
              <div className="price">{p.priceDisplay || (p.priceId ? p.priceId : 'Price not set')}</div>
              <div className="desc">{(p.description || '')}</div>
            </div>
          ))}
        </div>
      )}

      <form className="checkout-form__form" onSubmit={createSession}>
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>Choose plan</label>
          <select className="checkout-form__select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label} {p.priceDisplay ? `— ${p.priceDisplay}` : (p.priceId ? `— ${p.priceId}` : '— (price not set)')}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 6 }}>Email (optional)</label>
          <input className="checkout-form__input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </div>

        <div className="checkout-form__contract">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>I accept the membership contract and terms.</span>
          </label>
        </div>

        <div>
          <button className="checkout-form__submit" type="submit" disabled={busy || !planId || !hasSelectedPrice()}>
            {busy ? 'Processing…' : 'Continue to Secure Payment'}
          </button>
          {!hasSelectedPrice() && (
            <div style={{ marginTop: 8, color: '#fca5a5' }}>
              Selected plan does not have a Stripe price configured. Contact an admin to enable this plan.
            </div>
          )}
        </div>
      </form>

      {error && (
        <div style={{ marginTop: 12, color: 'crimson' }}>
          <strong>Error:</strong> {String(error)}
          {lastTriedUrl && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#7c3aed' }}>Tried: {lastTriedUrl}</div>
          )}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12, background: '#f6f7f9', padding: 12, borderRadius: 6 }}>
          <strong>Checkout session response</strong>
          <pre style={{ maxHeight: 320, overflow: 'auto' }}>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
